import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageSquare, Phone, CircleDot, Settings } from 'lucide-react';
import { useWebSocket } from '../context/WebSocketContext';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tabs = [
  { id: 'chats', label: 'Chats', icon: MessageSquare, path: '/chats' },
  { id: 'updates', label: 'Updates', icon: CircleDot, path: '/updates' },
  { id: 'calls', label: 'Calls', icon: Phone, path: '/calls' },
  { id: 'settings', label: 'Settings', icon: Settings, path: '/settings' },
];

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const { addMessageHandler } = useWebSocket();
  const [unreadCount, setUnreadCount] = useState(0);
  const [tappedTab, setTappedTab] = useState(null);
  const navRef = useRef(null);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API}/chats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const total = (res.data || []).filter(c => (c.unread_count || 0) > 0).length;
        setUnreadCount(total);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 10000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (!addMessageHandler) return;
    return addMessageHandler((data) => {
      if (data.type === 'new_message') {
        setUnreadCount(prev => prev + 1);
      } else if (data.type === 'messages_read' || data.type === 'message_read') {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    });
  }, [addMessageHandler]);

  const activeTab = tabs.find(t => location.pathname.startsWith(t.path))?.id || 'chats';

  const handleTabClick = (id, path) => {
    setTappedTab(id);
    setTimeout(() => setTappedTab(null), 200);
    navigate(path);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 overflow-hidden" data-testid="bottom-nav" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      {/* Night sky background */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, #0a0a1a 0%, #0d1b2a 50%, #1b2838 100%)' }}>
        {/* Stars */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(25)].map((_, i) => {
            const size = i % 5 === 0 ? 2 : 1;
            const isGold = i % 6 === 0;
            return (
              <div
                key={i}
                className="absolute rounded-full"
                style={{
                  width: `${size}px`,
                  height: `${size}px`,
                  left: `${(i * 29 + 5) % 97}%`,
                  top: `${(i * 19 + 8) % 85}%`,
                  opacity: 0.15 + (i % 4) * 0.15,
                  background: isGold ? 'rgba(212,175,55,0.9)' : 'white',
                  boxShadow: isGold ? '0 0 3px rgba(212,175,55,0.5)' : 'none',
                  animation: `navTwinkle ${2 + (i % 3) * 1.5}s ease-in-out infinite ${i * 0.4}s`
                }}
              />
            );
          })}
        </div>
        {/* Subtle aurora */}
        <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.25 }}>
          <div className="absolute w-[200%] h-full" style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(0,255,128,0.1) 20%, rgba(0,200,255,0.12) 40%, transparent 60%, rgba(212,175,55,0.08) 80%, transparent 100%)',
            animation: 'navAurora 18s ease-in-out infinite',
            filter: 'blur(12px)'
          }} />
        </div>
        {/* Top edge glow */}
        <div className="absolute top-0 left-0 right-0 h-[1px]" style={{
          background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.15), rgba(100,180,255,0.1), rgba(212,175,55,0.15), transparent)'
        }} />
      </div>

      <div className="relative z-10 flex items-center justify-around h-14 max-w-lg mx-auto" ref={navRef}>
        {tabs.map(({ id, label, icon: Icon, path }) => {
          const isActive = activeTab === id;
          const isTapped = tappedTab === id;
          return (
            <button
              key={id}
              onClick={() => handleTabClick(id, path)}
              className="relative flex flex-col items-center justify-center flex-1 h-full gap-0.5"
              data-testid={`nav-${id}`}
              style={{
                transform: isTapped ? 'scale(0.9)' : isActive ? 'scale(1.05)' : 'scale(1)',
                transition: 'transform 0.2s ease',
              }}
            >
              <div className="relative">
                <Icon
                  size={22}
                  strokeWidth={isActive ? 2.5 : 1.8}
                  className={isActive ? 'text-[#D4AF37] drop-shadow-[0_0_4px_rgba(212,175,55,0.5)]' : 'text-gray-500'}
                  style={{
                    transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.2s ease',
                    transform: isActive ? 'translateY(-1px)' : 'translateY(0)',
                  }}
                />
                {id === 'chats' && unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#D4AF37] text-white text-[10px] font-bold" data-testid="unread-badge">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              <span className={`text-[10px] font-medium transition-all duration-200 ${
                isActive ? 'text-[#D4AF37]' : 'text-gray-500 opacity-70'
              }`}>{label}</span>
            </button>
          );
        })}
      </div>

      <style>{`
        @keyframes navTwinkle { 0%, 100% { opacity: 0.1; } 50% { opacity: 0.8; } }
        @keyframes navAurora { 0% { transform: translateX(0%); } 50% { transform: translateX(-25%); } 100% { transform: translateX(0%); } }
      `}</style>
    </nav>
  );
};

export default BottomNav;
